// Pure helpers for the Saved Workouts page: name search, which cards a view
// shows, and which folder a duplicate must stay in. No DOM, no fetch.

export const normalizeWorkoutSearch = (search) =>
  String(search || "")
    .trim()
    .toLowerCase();

export const workoutMatchesName = (workout, search) => {
  const query = normalizeWorkoutSearch(search);
  if (!query) return true;
  return String(workout?.name || "")
    .toLowerCase()
    .includes(query);
};

export const filterWorkoutsByName = (workouts, search) => {
  const list = workouts || [];
  const query = normalizeWorkoutSearch(search);
  if (!query) return list;
  return list.filter((workout) => workoutMatchesName(workout, query));
};

export const uncategorizedWorkouts = (savedWorkouts, workoutFolders) => {
  const assignedIds = new Set(
    (workoutFolders || []).flatMap((folder) =>
      (folder.workoutIds || []).map((id) => String(id))
    )
  );
  return (savedWorkouts || []).filter(
    (workout) => !assignedIds.has(String(workout._id))
  );
};

export const workoutsInFolder = (savedWorkouts, folder) => {
  if (!folder) return [];
  const folderIds = new Set((folder.workoutIds || []).map((id) => String(id)));
  return (savedWorkouts || []).filter((workout) =>
    folderIds.has(String(workout._id))
  );
};

/**
 * Cards for the current Saved Workouts view.
 * A name search looks through every saved workout (including ones inside
 * folders). With no search, the landing page shows only uncategorized
 * workouts, and an open folder shows that folder's workouts.
 */
export const listSavedWorkoutsForView = ({
  savedWorkouts = [],
  workoutFolders = [],
  search = "",
  folder = null,
} = {}) => {
  if (folder) {
    return filterWorkoutsByName(
      workoutsInFolder(savedWorkouts, folder),
      search
    );
  }
  if (normalizeWorkoutSearch(search)) {
    return filterWorkoutsByName(savedWorkouts, search);
  }
  return uncategorizedWorkouts(savedWorkouts, workoutFolders);
};

/** The folder that already contains this workout, or null when it is uncategorized. */
export const folderForWorkout = (folders, workoutId) => {
  if (workoutId == null || workoutId === "") return null;
  const id = String(workoutId);
  return (
    (folders || []).find((folder) =>
      (folder?.workoutIds || []).some((memberId) => String(memberId) === id)
    ) || null
  );
};

const primitiveId = (value) => {
  if (typeof value === "string" && value) return value;
  if (typeof value === "number") return String(value);
  if (value && typeof value === "object" && value.$oid)
    return String(value.$oid);
  return "";
};

/**
 * Id of the cycle `cycle-duplicate-cycle` just created.
 * The endpoint is not documented in this repo; accept the shapes the other
 * cycle mutations already return (the document, or `{ cycle }`).
 */
export const duplicatedCycleId = (payload, seen = new Set()) => {
  if (payload == null || seen.has(payload)) return "";
  if (typeof payload !== "object") return primitiveId(payload);
  if (Array.isArray(payload)) return duplicatedCycleId(payload[0], seen);
  seen.add(payload);
  const own =
    primitiveId(payload._id) ||
    primitiveId(payload.id) ||
    primitiveId(payload.$oid) ||
    primitiveId(payload.cycleId) ||
    primitiveId(payload.newCycleId);
  if (own) return own;
  for (const key of ["cycle", "newCycle", "duplicatedCycle", "data"]) {
    const nested = duplicatedCycleId(payload[key], seen);
    if (nested) return nested;
  }
  return "";
};
